/**
 * AddRaceModal — Sprint 13 S13.10b (motor v10.1 §3).
 *
 * Modal para "añadir carrera" desde un Plan de Progresión activo. Formulario
 * único: distancia (5K-42K) + fecha + superficie (asfalto/trail). Submit →
 * POST /training-plans/active/add-race.
 *
 * Manejo del 409 `trail_modality_confirmation_required` (carrera trail con
 * perfil asfalto): se muestra un diálogo de confirmación ENCIMA del
 * formulario (no se navega fuera). El copy deja claro que confirmar pasa el
 * entrenamiento a MIXTO = mantiene base de asfalto + añade tirada larga en
 * monte (NO convierte todo a trail). Al confirmar, se reenvía el MISMO POST
 * con `confirm_trail_modality=true`. Cancelar cierra el diálogo sin mutar
 * nada (el backend no mutó nada en el 409).
 *
 * Distancias en MINÚSCULA al enviar (5k/10k/15k/half_marathon/marathon —
 * fix de casing S13.7 + enum del backend).
 */
import { useState } from "react";

import { addRaceToActivePlan } from "@/api/training";
import { t } from "@/i18n/es";
import { getAllowedRaceDistances } from "@/lib/raceDistance";
import { ApiError, type AthleteLevel } from "@/types/api";

const DISTANCE_OPTIONS: ReadonlyArray<{
  value: string;
  labelKey: keyof typeof t.plan;
}> = [
  { value: "5k", labelKey: "raceDistance5k" },
  { value: "10k", labelKey: "raceDistance10k" },
  { value: "15k", labelKey: "raceDistance15k" },
  { value: "half_marathon", labelKey: "raceDistanceHalfMarathon" },
  { value: "marathon", labelKey: "raceDistanceMarathon" },
];

/** Bounds del date picker: mañana .. hoy+365, en ISO LOCAL. */
function _dateBounds(): { minIso: string; maxIso: string } {
  const today = new Date();
  const min = new Date(today);
  min.setDate(today.getDate() + 1);
  const max = new Date(today);
  max.setDate(today.getDate() + 365);
  const toIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  return { minIso: toIso(min), maxIso: toIso(max) };
}

interface Props {
  onClose: () => void;
  /** Llamado tras añadir la carrera (201) — el contenedor recarga. */
  onAdded: () => void;
  /** v15.5-§8.4: nivel del atleta para filtrar el dropdown. Pasado por
   *  PlanPage (que ya fetchea onboarding). null = no se filtra
   *  (defensa — perfiles incompletos los handle otro gate). */
  athleteLevel?: AthleteLevel | null;
}

export function AddRaceModal({ onClose, onAdded, athleteLevel = null }: Props) {
  const [raceDistance, setRaceDistance] = useState("");
  const [raceDate, setRaceDate] = useState("");
  const [surface, setSurface] = useState<"asfalto" | "trail">("asfalto");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTrailConfirm, setShowTrailConfirm] = useState(false);

  const { minIso, maxIso } = _dateBounds();

  /** Envía el POST. `confirmTrail` se pasa a true en el reenvío tras el 409. */
  async function submit(confirmTrail: boolean) {
    setSubmitting(true);
    setError(null);
    try {
      await addRaceToActivePlan({
        race_distance: raceDistance,
        race_date: raceDate,
        surface,
        ...(confirmTrail ? { confirm_trail_modality: true } : {}),
      });
      onAdded();
      onClose();
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.code === "trail_modality_confirmation_required"
      ) {
        // Señal esperada (NO error): abrir diálogo de confirmación de
        // modalidad. NO reseteamos el formulario (se reenvía igual).
        setShowTrailConfirm(true);
      } else if (err instanceof ApiError && err.code === "beginner_max_5k") {
        // v15.5-§8.4: defensa backend disparada. El filtro frontend
        // debería haber prevenido el caso (PlanPage no debería ofrecer
        // distancias >5K a un beginner); si llegamos aquí es
        // desincronización (level cambió entre fetch y submit).
        setError(t.plan.errorBeginnerMax5k);
      } else {
        setError(t.plan.toastError);
      }
      setSubmitting(false);
    }
  }

  function onSubmitForm(e: React.FormEvent) {
    e.preventDefault();
    if (!raceDistance) {
      setError(t.plan.errorRaceDistanceRequired);
      return;
    }
    if (!raceDate) {
      setError(t.plan.errorRaceDateRequired);
      return;
    }
    void submit(false);
  }

  return (
    <div
      data-testid="add-race-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-race-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-carbon-raised p-5">
        <h2
          id="add-race-modal-title"
          className="text-lg font-medium text-bone"
        >
          {t.plan.addRaceTitle}
        </h2>

        <form
          data-testid="add-race-form"
          onSubmit={onSubmitForm}
          className="mt-4 flex flex-col gap-4"
          noValidate
        >
          <div className="flex flex-col gap-2">
            <label
              htmlFor="add-race-distance"
              className="text-xs uppercase tracking-widest text-gray-mid"
            >
              {t.plan.createRaceDistanceLabel}
            </label>
            <select
              id="add-race-distance"
              data-testid="add-race-distance-select"
              value={raceDistance}
              onChange={(e) => setRaceDistance(e.target.value)}
              required
              className="rounded-md border border-border-subtle bg-graphite px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
            >
              <option value="">—</option>
              {/* v15.5-§8.4: filtrado por nivel (beginner → solo "5k"). */}
              {DISTANCE_OPTIONS.filter((opt) =>
                getAllowedRaceDistances(
                  athleteLevel,
                  DISTANCE_OPTIONS.map((o) => o.value),
                ).includes(opt.value),
              ).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t.plan[opt.labelKey]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="add-race-date"
              className="text-xs uppercase tracking-widest text-gray-mid"
            >
              {t.plan.createRaceDateLabel}
            </label>
            <input
              id="add-race-date"
              data-testid="add-race-date-input"
              type="date"
              value={raceDate}
              min={minIso}
              max={maxIso}
              onChange={(e) => setRaceDate(e.target.value)}
              required
              className="rounded-md border border-border-subtle bg-graphite px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
            />
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs uppercase tracking-widest text-gray-mid">
              {t.plan.addRaceSurfaceLabel}
            </legend>
            <div className="flex gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-bone">
                <input
                  type="radio"
                  name="add-race-surface"
                  value="asfalto"
                  data-testid="add-race-surface-asfalto"
                  checked={surface === "asfalto"}
                  onChange={() => setSurface("asfalto")}
                />
                {t.plan.addRaceSurfaceAsphalt}
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-bone">
                <input
                  type="radio"
                  name="add-race-surface"
                  value="trail"
                  data-testid="add-race-surface-trail"
                  checked={surface === "trail"}
                  onChange={() => setSurface("trail")}
                />
                {t.plan.addRaceSurfaceTrail}
              </label>
            </div>
          </fieldset>

          {error && (
            <p
              data-testid="add-race-error"
              role="alert"
              className="text-sm text-danger"
            >
              {error}
            </p>
          )}

          <div className="mt-1 flex items-center justify-end gap-3">
            <button
              type="button"
              data-testid="add-race-cancel"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.plan.skipModalCancel}
            </button>
            <button
              type="submit"
              data-testid="add-race-submit"
              disabled={submitting}
              className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.plan.addRaceSubmit}
            </button>
          </div>
        </form>
      </div>

      {/* Diálogo de confirmación de modalidad trail (409). Encima del
          formulario. Copy claro: pasa a MIXTO. */}
      {showTrailConfirm && (
        <div
          data-testid="trail-modality-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="trail-modality-title"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
        >
          <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-carbon-raised p-5">
            <h3
              id="trail-modality-title"
              className="text-lg font-medium text-bone"
            >
              {t.plan.trailModalityTitle}
            </h3>
            <p className="mt-2 text-sm text-bone-muted">
              {t.plan.trailModalityBody}
            </p>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                data-testid="trail-modality-cancel"
                onClick={() => setShowTrailConfirm(false)}
                disabled={submitting}
                className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.trailModalityCancel}
              </button>
              <button
                type="button"
                data-testid="trail-modality-confirm"
                onClick={() => void submit(true)}
                disabled={submitting}
                className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.trailModalityConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
